import { firestore } from 'firebase';
import { Duration } from 'luxon';

export type OnboardingStage =
  | 'Waiting'
  | 'Detection'
  | 'Transmission'
  | 'Processing'
  | 'Storage'
  | 'Complete';

export interface OnboardingProgress {
  countdown: string;
  isOnboarding: boolean;
  progress: number;
  stage: [OnboardingStage, string];
}

export const PROGRESS_LENGTH = 3.6e6;

const stagesOrder: OnboardingStage[] = [
  'Waiting',
  'Detection',
  'Transmission',
  'Processing',
  'Storage',
  'Complete',
];

const stages: [OnboardingStage, string][] = [
  ['Detection', 'Detecting transmission from distant lifeform'],
  ['Detection', 'Adjusting the Gemini satellite'],
  ['Detection', 'Scanning'],
  ['Detection', 'Honing in on Coma Berenices'],
  ['Detection', 'Calculating angle using Oort constants'],
  ['Detection', 'Adjusting to accounts for parallax shift'],
  ['Detection', 'Locking on'],
  ['Detection', 'Location detected Gamma Comae'],

  ['Transmission', 'Focusing LBN for optional signal strength'],
  ['Transmission', 'Handshake packet received'],
  ['Transmission', 'Calculated transmission distance 166.87 light-years'],
  ['Transmission', 'Establishing SSSL (Secure-Space-Socket-Connection)'],
  ['Transmission', 'Phase 1 - Establishing secure capabilities'],
  ['Transmission', 'Phase 2 - Quantum authentication key exchange'],
  ['Transmission', 'Phase 3 - Finalising handshake'],
  ['Transmission', 'Phase 4 - Connection docking complete'],
  ['Transmission', 'Packet 42 or 42,342,543,236,877,765'],
  ['Transmission', 'Transmission received'],

  ['Processing', 'Detecting payload format'],
  ['Processing', 'Processing format header'],
  ['Processing', 'Decompressing payload body'],
  ['Processing', 'Deciphering entries'],
  ['Processing', 'Validating'],
  ['Processing', 'Translating for extraterrestrial query optimization'],
  ['Processing', 'Preparing the data model'],
  ['Processing', 'Detecting insights'],

  ['Storage', 'Powering up active-active storage service'],
  ['Storage', 'Setting up end-to-end storage encryption'],
  ['Storage', 'Uploading stellar classification data'],
  ['Storage', 'Rebalancing K-Type data index'],
  ['Storage', 'Submitting new payload transaction'],
  ['Storage', 'Validating checksum'],
  ['Storage', 'Storage complete'],
];

const onboardingNotStarted: OnboardingProgress = {
  countdown: '0m 0s',
  isOnboarding: false,
  progress: 0,
  stage: ['Waiting', 'Waiting'],
};

export const isStageComplete = (stageA: OnboardingStage, stageB: OnboardingStage): boolean =>
  stagesOrder.indexOf(stageA) > stagesOrder.indexOf(stageB);

export default function getOnboardingProcess(timestamp?: firestore.Timestamp): OnboardingProgress {
  if (!timestamp) {
    return onboardingNotStarted;
  }

  const duration = Math.max(0, Math.min(Date.now() - timestamp.toMillis(), PROGRESS_LENGTH));
  const progress = Math.max(0, Math.min(duration / PROGRESS_LENGTH, 1));
  const stage = stages[Math.floor((stages.length + 1) * progress)] || stages[stages.length - 1];

  return {
    countdown: Duration.fromMillis(PROGRESS_LENGTH - duration).toFormat("m'm' s's'"),
    isOnboarding: progress < 1,
    progress,
    stage: progress < 1 ? stage : ['Complete', 'Complete'],
  };
}
